import { dsClass } from '../../common/constants/dsClasses';
import { monthsMsg, pcsMsg, t, totalMsg } from '../../common/i18n/index.js';
import {
  getCommercialProductPrices,
  selectNonOpeningFeeCommercialProductCode,
} from '../../common/utils/commercialProductUtils.js';
import { formatSum, formatSumToString } from '../../common/utils/priceUtils.js';
import { isSalesProduct } from '../../common/utils/subscriptionUtils';
import { getVatAsString } from '../../common/utils/taxUtils.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { ConfiguredCommercialProduct } from '../../common/types/commercialProduct.js';
import type { TotalSumsItem } from '../../common/utils/commercialProductUtils.js';
import type { CampaignAssociation, SalesType } from '../../generated/api/models.js';
import type { JSX } from 'react';

export type RowItem = {
  amount: string | JSX.Element;
  className?: string;
  key: string;
  monthlyCharge: string | JSX.Element;
  name: string | JSX.Element;
  onetimeCharge: string | JSX.Element;
};
export const getTotalRows = (
  totalSums: TotalSumsItem,
  priceIncludesVAT?: boolean,
  orderContainsBillingPeriodItem = false
): RowItem[] => {
  const { totalMonthlyRecurringCharge, totalMonthlyRecurringChargeVat, totalOneTimeCharge, totalOneTimeChargeVat } =
    totalSums;
  const vatAsString = getVatAsString();
  const monthlyCharge = orderContainsBillingPeriodItem
    ? formatSumToString(totalMonthlyRecurringCharge)
    : t.YO7F('{}/Month', formatSumToString(totalMonthlyRecurringCharge));

  const totalVat = orderContainsBillingPeriodItem
    ? formatSumToString(totalMonthlyRecurringChargeVat)
    : t.YO7F('{}/Month', formatSumToString(totalMonthlyRecurringChargeVat));

  const totalIncludingVat = orderContainsBillingPeriodItem
    ? formatSumToString(totalMonthlyRecurringChargeVat + totalMonthlyRecurringCharge)
    : t.YO7F('{}/Month', formatSumToString(totalMonthlyRecurringChargeVat + totalMonthlyRecurringCharge));

  const totalItem = {
    className: 'of-detailed-summary-table__total-row-item of-detailed-summary-table__total-row-header',
    key: 'total',
    name: (
      <strong>
        {t.CEQ2(totalMsg)} {t.E4TT('(VAT {}%)', priceIncludesVAT ? vatAsString : '0')}
      </strong>
    ),
    amount: '',
    onetimeCharge: <strong>{formatSum(totalOneTimeCharge) || ''}</strong>,
    monthlyCharge: <strong>{monthlyCharge}</strong>,
  };

  if (priceIncludesVAT) {
    return [totalItem];
  }

  return [
    totalItem,
    {
      className: 'of-detailed-summary-table__total-row-item',
      key: 'total-alv',
      name: t.A0OJ('VAT {}%', vatAsString),
      amount: '',
      onetimeCharge: formatSum(totalOneTimeChargeVat) || '',
      monthlyCharge: totalVat,
    },
    {
      className: 'of-detailed-summary-table__total-row-item',
      key: 'total-with-alv',
      name: t.YXMI('Price including taxes'),
      amount: '',
      onetimeCharge: formatSum(totalOneTimeCharge + totalOneTimeChargeVat) || '',
      monthlyCharge: totalIncludingVat,
    },
  ];
};

export const getCommercialProductRowItem = (
  configuredCommercialProduct: ConfiguredCommercialProduct,
  amount: number
): RowItem => {
  const { commercialProduct } = configuredCommercialProduct;
  const billingPeriod = commercialProduct.billingPeriod;
  const priceSuffix = billingPeriod ? `/${billingPeriod} ${t.XXVX(monthsMsg)}` : '';
  const monthlyCharge = commercialProduct.monthlyRecurringCharge
    ? `${formatSum(commercialProduct.monthlyRecurringCharge * amount)}${priceSuffix}`
    : '';
  const onetimeCharge = commercialProduct.oneTimeCharge ? `${formatSum(commercialProduct.oneTimeCharge * amount)}` : '';

  const key = isSalesProduct(commercialProduct)
    ? (selectNonOpeningFeeCommercialProductCode(configuredCommercialProduct.commercialProductCodes) as string)
    : commercialProduct.commercialProductCode;

  return {
    key,
    name: commercialProduct.commercialProductName,
    amount: `${amount} ${t.B3MG(pcsMsg)}`,
    onetimeCharge,
    monthlyCharge,
  };
};

export const getCampaignDiscountRows = (
  configuredCommercialProducts: ConfiguredCommercialProduct[],
  salesType: SalesType,
  campaignDescription?: string,
  campaignAssociation?: CampaignAssociation
): RowItem[] => {
  if (!configuredCommercialProducts || configuredCommercialProducts.length === 0) {
    return [];
  }

  // Group by product code and campaign description
  const productGroups: Record<string, ConfiguredCommercialProduct[]> = {};

  for (const confCp of configuredCommercialProducts) {
    const cp = confCp.commercialProduct;
    if (!campaignAssociation && !confCp.deviceVoucher && !confCp.campaignDescription) {
      continue;
    }

    const effectiveCampaignDescription = campaignDescription || confCp.campaignDescription || '';
    const key = `${cp.commercialProductCode}-${effectiveCampaignDescription}`;

    if (!productGroups[key]) {
      productGroups[key] = [];
    }
    productGroups[key].push(confCp);
  }

  return Object.entries(productGroups).map(([key, products]) => {
    const cp = products[0].commercialProduct;
    const voucher = products[0].deviceVoucher;
    const quantity = products.length;

    const effectiveCampaignDescription = voucher
      ? voucher.campaignDescription
      : campaignDescription || products[0].campaignDescription || '';

    const { oneTimeCharge, monthlyRecurringCharge, oneTimeChargeBeforeDiscount, monthlyRecurringChargeBeforeDiscount } =
      getCommercialProductPrices(cp, campaignAssociation, salesType);

    // campaignAssociation charges are already the discount amount (not the effective price)
    const getDiscountAmount = (
      campaignCharge: number | undefined,
      chargeAfterDiscount: number | undefined,
      chargeBeforeDiscount: number | undefined
    ) => {
      if (campaignAssociation) {
        return (campaignCharge ?? 0) * quantity;
      }
      if (chargeAfterDiscount && chargeBeforeDiscount) {
        return (chargeBeforeDiscount - chargeAfterDiscount) * quantity;
      }
      return 0;
    };

    const onetimeDiscountAmount = getDiscountAmount(
      campaignAssociation?.oneTimeCharge,
      oneTimeCharge,
      oneTimeChargeBeforeDiscount
    );
    const monthlyDiscountAmount = getDiscountAmount(
      campaignAssociation?.monthlyRecurringCharge,
      monthlyRecurringCharge,
      monthlyRecurringChargeBeforeDiscount
    );

    const onetimeDiscount = onetimeDiscountAmount > 0 ? `-${formatSum(onetimeDiscountAmount)}` : '';
    const monthlyDiscount = monthlyDiscountAmount > 0 ? `-${formatSum(monthlyDiscountAmount)}` : '';

    return {
      key,
      name: (
        <div className={`${dsClass.PADDING_LEFT_3} ${dsClass.FONT_WEIGHT_400}`}>{effectiveCampaignDescription}</div>
      ),
      amount: voucher ? '' : `${quantity} ${t.B3MG(pcsMsg)}`,
      onetimeCharge: onetimeDiscount,
      monthlyCharge: monthlyDiscount,
    };
  });
};

export const getAddedAddonRowItem = (
  commercialProductCode: string,
  addedAddon: AddedAddon,
  amount: number
): RowItem => ({
  key: `${commercialProductCode}-${addedAddon.addOnCode}`,
  name: addedAddon.displayName,
  amount: `${amount} ${t.B3MG(pcsMsg)}`,
  onetimeCharge: formatSum(addedAddon.oneTimePrice ? addedAddon.oneTimePrice * amount : undefined) || '',
  monthlyCharge: formatSum(addedAddon.monthlyPrice ? addedAddon.monthlyPrice * amount : undefined) || '',
});

export const getDeliveryChargesRow = (deliveryCharges?: number, hideZeroDeliveryCharges?: boolean): RowItem[] => {
  if (hideZeroDeliveryCharges && !deliveryCharges) {
    return [];
  }

  return [
    {
      key: 'delivery-charges',
      name: t.PFDS('Delivery charges'),
      amount: '',
      onetimeCharge: formatSumToString(deliveryCharges || 0),
      monthlyCharge: '',
    },
  ];
};
